import * as React from "react";
import { Thread } from "@/components/assistant-ui/thread";

import { Button, Popover } from "antd";
import { useAlphaAgentStore } from "@/state/alphaAgentStore";
import { StepIndicator } from "@/components/ui/StepIndicator";
import clsx from "clsx";
import { getSelectedShape } from "@/utils/sharePricePerformance/selectedPlaceHolderLocalStorage";
import { CSSTransition } from "react-transition-group";
import { labels } from "@/utils/alphaAgent/helper";
import { resetLLM } from "@/utils/alphaAgent/resetLLM";
import { addFieldsToData, transformGlobalState } from "@/utils/alphaAgent/modelDataHelper";
import { getToken } from "@/utils/localstorage";
import axios from "axios";
import UIFormatTemplateSelection from "@alphascriptsolutions/client-core/dist/uiFormatTemplateSelection";
import { MyRuntimeProviderAlphaCreate } from "@/placeholderPopup/components/MyRuntimeProvider";
import CustomFeatures from "@/placeholderPopup/components/CustomFeatures";
import { MyRuntimeProviderFeatureDetails } from "@/placeholderPopup/components/MyRuntimeProviderFeatureDetails";
import FormatAndPlaceholder from "@/placeholderPopup/components/FormatAndPlaceholder";
import { usePlaceholderPopupStore } from "@/state/placeholderPopupStore";
import { getCandidatePlaceholders } from "@/utils/candidatePlaceholders";
import { clearPositioningLocalStorage } from "@/utils/clearLocalStorage";
import { logger } from "@/helpers/tslog";
import { getFormatAndPlaceholderValues } from "@/utils/alphaAgent/FormatAndPlaceholderUtils";

function Step({ show, children }) {
  const nodeRef = React.useRef<HTMLDivElement | null>(null);
  return (
    <CSSTransition
      in={show} // ← visible or hidden?
      timeout={150}
      classNames="step" // ← base for all generated class-names
      mountOnEnter={false} // keep it in the DOM from the first render
      unmountOnExit={false} // never un-mount (so state survives)
      appear={show}
      nodeRef={nodeRef}
    >
      <div ref={nodeRef} className="step-slot">{children}</div>
    </CSSTransition>
  );
}

const AlphaPopup: React.FC = () => {
  const {
    currentStep,
    questionsPerStep,
    setCurrentStep,
    setDisabledButton,
    setIsChatCompleted,
    isChatCompleted,
    disableBackButton,
  } = useAlphaAgentStore();
  const disabledButton = useAlphaAgentStore((state) => state.disabledButton);
  const answersByStep = useAlphaAgentStore((state) => state.answersByStep);
  const { setSelectedContent, setShowSidebar } = usePlaceholderPopupStore();
  const getMessage = () => {
    if (currentStep === 1) {
      return "Going back to step 1 will remove all entries completed on the Feature Details page.";
    } else if (currentStep === 2) {
      return "Going back to Step 2 will clear only the current feature’s configuration.";
    }
    return "";
  };
  const handleNextClick = React.useCallback(async () => {
    const { isEditInitial } = useAlphaAgentStore.getState();
    if (currentStep === 3) {
      const rawData = localStorage.getItem("alphaCreateData");
      const globalStateData = JSON.parse(rawData);
      const convertedDataToRequiredForm = transformGlobalState(globalStateData);
      const { model } = useAlphaAgentStore.getState();

      const presentationDesign = model.getByName<UIFormatTemplateSelection>("presentationDesign");

      const selectedPlaceholder = getSelectedShape();
      const { title: candidateTitleShape, subTitle: candidateSubTitleShape, closestGap: candidateClosestGap } = getCandidatePlaceholders();
      const { title, subTitle, source, notes } = getFormatAndPlaceholderValues();
      const tradingVolumeBool = localStorage.getItem("tradingVolume") === "true";
      const completeData = addFieldsToData({
        chartObject: convertedDataToRequiredForm,
        customFormatId: presentationDesign.value,
        customPlaceholder: selectedPlaceholder,
        titlePlaceholderShape: candidateTitleShape,
        subTitlePlaceholderShape: candidateSubTitleShape,
        metadata: {
          chartTitle: {
            enabled: true,
            value: title,
          },
          chartSubTitle: {
            enabled: true,
            value: subTitle,
          },
          source: {
            enabled: true,
            value: source,
          },
          notes: {
            enabled: true,
            value: notes,
          },
          placeholderGap: candidateClosestGap,
        },
        tradingVolume: tradingVolumeBool,
        chartSubtitleRight: tradingVolumeBool ? true : undefined,
      });
      logger.info("completeData", {
        completeData,
      });
      localStorage.setItem("ModelDataToBackend", JSON.stringify(completeData));
      localStorage.setItem("modelGlobalState", rawData);
      clearPositioningLocalStorage();
      Office.context.ui.messageParent("INSERT_CHART");
      localStorage.removeItem("messages");
      setIsChatCompleted(true);
      await resetLLM();
      return;
    }
    if (currentStep === 0 && isEditInitial) {
      const apiUrl = process.env.API_URL;
      const authToken = await getToken();
      await axios.post(
        `${apiUrl}/alpha-create/interact`,
        { jumpToStep: 2 },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
    }
    setCurrentStep(currentStep + 1);
    const { setIsEditInitial } = useAlphaAgentStore.getState();
    setIsEditInitial(false);
    setDisabledButton(true);
  }, [currentStep, setCurrentStep, setDisabledButton, setIsChatCompleted]);

  if (isChatCompleted) {
    return (
      <div
        style={{
          boxSizing: "border-box",
          padding: "0 50px",
          fontWeight: "bold",
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          gap: "10px",
        }}
      >
        <p className="text-gray-800">
          Thank you. We are working on the Excel model and placeholder content you requested. We estimate this to take 1
          – 2 minutes. Your Taskpane will open when the content is ready.
        </p>
        <Button type="primary" size="large" onClick={() => Office.context.ui.messageParent("CLOSE")}>
          Close
        </Button>
      </div>
    );
  }

  const handleBackToLibrary = async () => {
    try {
      setShowSidebar(true);
      setSelectedContent(null);
      await resetLLM();
      localStorage.removeItem("messages");
    } catch (e) {}
  };

  return (
    <div
      className={clsx(
        "flex flex-col justify-between h-full min-h-screen grid-rows-[auto_1fr_auto] gap-x-2 px-4 py-4",
        currentStep === 3 && "bg-gray-100"
      )}
    >
      <img src="assets/AlphaScript_Logo_Dark_cropped.png" alt="" className="w-36 h-auto object-contain mb-2" />
      <div
        className={clsx(
          "w-full mx-auto max-w-[var(--thread-max-width)] py-4 shadow-md px-4 rounded-sm z-10 border border-gray-200 border-b-[2px] border-b-[#ff9336] bg-white"
        )}
      >
        <StepIndicator
          currentStep={currentStep}
          answersByStep={answersByStep}
          questionsPerStep={questionsPerStep}
          labels={labels}
        />
      </div>

      <div className={"grid h-full relative w-full"}>
        <Step show={currentStep === 0}>
          <MyRuntimeProviderAlphaCreate>
            <Thread />
          </MyRuntimeProviderAlphaCreate>
        </Step>

        <Step show={currentStep === 1}>
          <CustomFeatures />
        </Step>

        <Step show={currentStep === 2}>
          <MyRuntimeProviderFeatureDetails>
            <Thread />
          </MyRuntimeProviderFeatureDetails>
        </Step>

        <Step show={currentStep === 3}>
          <FormatAndPlaceholder />
        </Step>
      </div>
      <div className="flex justify-end  w-full px-2 pt-4 mx-auto max-w-[var(--thread-max-width)]">
        <div className="flex gap-2 items-center">
          {currentStep === 0 && (
            <Button type="default" onClick={handleBackToLibrary}>
              Back
            </Button>
          )}
          {currentStep !== 0 && currentStep !== 3 && (
            <Popover
              content={
                <div style={{ width: "250px" }}>
                  <p>{getMessage()}</p>
                </div>
              }
              title="Are you sure?"
              trigger="hover"
            >
              <Button
                type="default"
                disabled={disableBackButton}
                onClick={async () => {
                  const { currentStep, setCurrentStep, setIsEditInitial } = useAlphaAgentStore.getState();
                  if (currentStep > 0) setCurrentStep(currentStep - 1);
                  if (currentStep === 1) {
                    setIsEditInitial(true);
                  }
                  const apiUrl = process.env.API_URL;
                  const authToken = await getToken();
                  await axios.post(
                    `${apiUrl}/alpha-create/interact`,
                    { jumpToStep: currentStep },
                    {
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${authToken}`,
                      },
                    }
                  );
                }}
              >
                Back
              </Button>
            </Popover>
          )}

          <Button
            type={"primary"}
            disabled={disabledButton && answersByStep[currentStep] !== questionsPerStep[currentStep]}
            className={""}
            onClick={handleNextClick}
          >
            {currentStep === 3 ? "Complete" : "Next"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AlphaPopup;
